import timeRangeAccordionGroupTemplate from './time_range_accordion_group.html';
import './time_range_accordion_group.scss';

// File needed in the template.
import './time_range_accordion_group_transclude.html';

/***
 * @ngdoc component
 * @name BBPublicBooking:bbAccordionRangeGroup
 *
 * @description
 * Use to group TimeSlot's by specified range for use with AngularUI Bootstrap accordion control
 *
 * @param {Object} options Object representing additional options.
 *
 *   - **header** – `{String}` – Translation key for the accordion group header.
 *   - **range** – `{Array}` – Array used to deduce accordion time ranges. First element is start, second is an end.
 *   - **flags** – `{Array}` – Array of strings key for additional boolean configuration.
 *     ['disguiseAvailabilityData'] – With this flag on availability data will be hidden in a header.
 *     ['collapseWhenTimeSelected'] – With this flag on accordion will roll up after selecting a slot.
 *
 * @param {Day} day Day for which slots will be drawn.
 * @param {Array} slots List of all slots that can be rendered.
 * @param {function(TimeSlot)} selectSlot Callback function used when slot is selected.
 * @param {TimeSlot} disabledSlot ?
 */

const bbTimeRangeAccordionGroup = {
    bindings: {
        options: '<',
        day: '<',
        slots: '<',
        selectSlot: '<',
        disabledSlot: '=disabledSlot'
    },
    templateUrl: timeRangeAccordionGroupTemplate.id,
    controller: AccordionRangeGroupController,
};

function AccordionRangeGroupController($scope, $rootScope, $element, DateTimeUtilitiesService, $translate,
    CompanyStoreService, bbTimeZone, $filter, bbCalendarUtility) {

    const $ctrl = this;

    ///////////////////

    let rangeStart;
    let rangeLimit;
    let deregisterSlotChangedListener;

    $scope.$on('time_slots:loaded', initialize);
    $scope.$on('$destroy', deregisterSlotChangedListener);

    /***
     * @ngdoc method
     * @name $onInit
     * @methodOf BB.Components:bbAccordionRangeGroup
     *
     * @description
     * Initialization process of the component.
     */
    $ctrl.$onInit = () => {
        initialize();
        $rootScope.connection_started.then(initialize);
    };

    function initialize() {
        if (!bbCalendarUtility.canMakePastBookings()) {
            const now = moment();
            $ctrl.slots = $ctrl.slots.filter(slot => slot.datetime.isAfter(now));
        }

        // Copying option object not to trigger any undesired changes.
        $ctrl.options = angular.copy($ctrl.options);

        rangeStart = $ctrl.options.range[0];
        rangeLimit = $ctrl.options.range[1];

        if ($ctrl.options.flags) {
            $ctrl.options.flags.forEach(flag => $ctrl.options[flag] = true);
        }

        $ctrl.heading = $translate.instant($ctrl.options.heading);
        setData();

        if (deregisterSlotChangedListener) {
            deregisterSlotChangedListener();
        }

        deregisterSlotChangedListener = $rootScope.$on('slotChanged', (event, day, slot) => slotSelected(day, slot));
    }

    $ctrl.$onChanges = (changes) => {
        if (changes.slots && !changes.slots.isFirstChange()) {
            setData();
        }
    };


    /***
     * @ngdoc method
     * @name setData
     * @methodOf BBPublicBooking:bbAccordionRangeGroup
     *
     * @description
     * Sets slots and updates availability depending on given slots.
     */
    function setData() {
        $ctrl.accordionSlots = [];
        if ($ctrl.slots) {
            $ctrl.slots.forEach((slot) => {
                // Use display time zone to ensure slots get added to the correct range group.
                const slotTime = getSlotTime(slot);

                if (slotTime >= rangeStart && slotTime < rangeLimit && slot.avail === 1) {
                    $ctrl.accordionSlots.push(slot);
                }
            });

            updateAvailability();
        }
    }

    /***
     * @ngdoc method
     * @name updateAvailability
     * @methodOf BBPublicBooking:bbAccordionRangeGroup
     *
     * @description
     * Update availability of the slots.
     */
    function updateAvailability() {
        $ctrl.selectedSlot = null;
        $ctrl.hasAvailability = hasAvailability();

        // TODO: Check whether disabledSlot is used at all.

        if ($ctrl.disabledSlot && $ctrl.disabledSlot.time) {
            if ($ctrl.disabledSlot.date === $ctrl.day.date.toISODate()) {
                let relevantSlot;

                if (Array.isArray($ctrl.disabledSlot.time)) {
                    for (const times of Array.from($ctrl.disabledSlot.time)) {
                        relevantSlot = _.findWhere($ctrl.slots, { time: times });
                        if (relevantSlot) {
                            relevantSlot.disabled = true;
                        }
                    }
                } else {
                    relevantSlot = _.findWhere($ctrl.slots, { time: $ctrl.disabledSlot.time });
                    if (relevantSlot) {
                        relevantSlot.disabled = true;
                    }
                }
            }
        }

        $ctrl.selectedSlot = $ctrl.accordionSlots.find(slot => slot.selected);

        // Aria-label message generated dynamically from the JS code. Slots number + heading.
        const slotsAvailable = $translate.instant(
            'PUBLIC_BOOKING.ACCORDION_RANGE_GROUP.NUM_SLOTS_AVAILABLE_ARIA_LABEL', {
                slotsNumber: $ctrl.accordionSlots.length
            }).concat($ctrl.heading);

        $element.attr('aria-label', slotsAvailable);
    }

    function slotSelected(day, slot) {
        $ctrl.selectedSlot = null;

        // If a day and slot have been provided, check if the slot is in range and mark it as selected.
        if (day && slot) {
            // Use display time zone to ensure slots are added to the right range group.
            const slotTime = getSlotTime(slot);

            if (day.date.isSame($ctrl.day.date, 'day') && (slotTime >= rangeStart) && (slotTime < rangeLimit)) {
                $ctrl.selectedSlot = slot;
            }
        } else {
            $ctrl.selectedSlot = $ctrl.accordionSlots.find(slot => slot.selected);
        }

        if ($ctrl.options.collapseWhenTimeSelected) {
            $ctrl.isOpen = false;
        }
    }

    function getSlotTime(slot) {
        const displayTimeZone = bbTimeZone.getDisplay();
        if (displayTimeZone !== null && displayTimeZone !== CompanyStoreService.time_zone) {
            const datetime = moment(slot.datetime).tz(displayTimeZone);
            return DateTimeUtilitiesService.convertMomentToTime(datetime);
        } else {
            return slot.time;
        }
    }

    /***
     * @ngdoc method
     * @name hasAvailability
     * @methodOf BBPublicBooking:bbAccordionRangeGroup
     *
     * @description
     * Verify whether the accordion has any available slot or not.
     */
    function hasAvailability() {
        return !!$ctrl.accordionSlots.find(slot => slot.availability());
    }

    $scope.toggle = () => {
        calculateTransitionDurationStyle();
    };

    $scope.generateAriaSlotMessage = (slot) => {
        const translated = slot.selected
            ? $translate.instant('ADMIN_BOOKING.CALENDAR.SELECTED_SLOT')
            : $translate.instant('ADMIN_BOOKING.CALENDAR.SELECT_SLOT');

        return translated + ' ' + $filter('datetime')(slot.datetime, 'h:mm a D MMMM');
    };

    /***
     * @ngdoc method
     * @name calculateTransitionDurationStyle
     * @methodOf BBPublicBooking:bbAccordionRangeGroup
     *
     * @description
     * Calculates animation time for accordion range group when collapsing. This function has to be assigned to local
     * scope, so that third party code can use it. See time_range_accordion_group_transclude.html template file.
     */
    function calculateTransitionDurationStyle() {
        const panelCollapseSibling = $element.find('[uib-collapse]');
        if (panelCollapseSibling) {
            panelCollapseSibling.css('transition-duration', calculateTransitionDurationStyleValue());
        }
    }

    function calculateTransitionDurationStyleValue() {
        // List unfolding should last from 0.4s up to 2s for 400 elements. Half that time for 20.
        return `${Math.max(Math.log($ctrl.accordionSlots.length) / Math.log(20) * 1000, 400)}ms`;
    }
}

angular
    .module('BBPublicBooking')
    .component('bbTimeRangeAccordionGroup', bbTimeRangeAccordionGroup);



// WEBPACK FOOTER //
// node_modules/bookingbug-angular/src/public-booking/accordion-range-group/v2/time_range_accordion_group.component.js